/**
 * Native Web Push (VAPID) subscription management. The browser
 * talks to its own push service; we only need the VAPID public key
 * (VITE_UGI_VAPID_PUBLIC_KEY) and the service worker in
 * `src/service-workers/sw.ts`.
 */

import { readonly, ref } from 'vue';

export interface PushSubscriptionPayload {
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent: string;
}

/**
 * Why this browser could not be subscribed. The switch says which one it was:
 * «дозволь сповіщення» is useless advice to someone whose build has no key.
 */
export type PushSubscribeFailure = 'unsupported' | 'not-configured' | 'denied' | 'failed';

export type PushSubscribeResult
  = | { ok: true; subscription: PushSubscriptionPayload }
    | { ok: false; reason: PushSubscribeFailure };

const urlBase64ToUint8Array = (base64: string): Uint8Array<ArrayBuffer> => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const output = new Uint8Array(raw.length);

  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }

  return output;
};

const registerServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  const isLocal = import.meta.env.VITE_APP_ENV === 'local';
  const swUrl = isLocal ? '/dev-sw.js?dev-sw' : '/sw.js';
  const swType: WorkerType = isLocal ? 'module' : 'classic';

  await navigator.serviceWorker.register(swUrl, {
    type: swType,
  });

  // Resolves once the registration at this scope has an active worker.
  return await navigator.serviceWorker.ready;
};

const isSupported = typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'Notification' in window
  && 'PushManager' in window;

/** Empty key = this build was made without Web Push; nothing can subscribe. */
const vapidPublicKey = (import.meta.env.VITE_UGI_VAPID_PUBLIC_KEY ?? '').trim();
const isConfigured = vapidPublicKey !== '';

/**
 * Whether a subscription was made with OUR key. A browser that had push on
 * before the move to VAPID still holds a subscription bound to the old
 * key: the API cannot push to it, and `pushManager.subscribe()` with another
 * key throws while it exists. Such a subscription counts as "none" and is
 * dropped before subscribing anew.
 */
const isOwnSubscription = (subscription: PushSubscription): boolean => {
  const key = subscription.options.applicationServerKey;

  if (!key || !isConfigured) {
    return isConfigured;
  }

  const actual = new Uint8Array(key);
  const expected = urlBase64ToUint8Array(vapidPublicKey);

  return actual.length === expected.length
    && actual.every((byte, index) => byte === expected[index]);
};

/**
 * Whether THIS browser currently holds an active push subscription — module
 * scoped (not inside the composable factory) so every `usePushSubscription()`
 * caller shares one value: PushChannelCard updates it after a toggle, and the
 * rest of the settings page reflects that change immediately, with no event
 * wiring and no reload.
 */
const deviceSubscribed = ref(false);

export const usePushSubscription = () => {
  /**
   * Endpoint of this browser's current push subscription without touching
   * it — lets callers unregister on the API first and only then drop the
   * local subscription.
   */
  const getSubscribedEndpoint = async (): Promise<string | null> => {
    if (!isSupported) {
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();

      return subscription && isOwnSubscription(subscription) ? subscription.endpoint : null;
    } catch (error) {
      console.error(error);

      return null;
    }
  };

  /**
   * Refresh `isDeviceSubscribed` from the browser's actual state — read-only,
   * never prompts. Call after mount and after any register/unregister
   * mutation resolves.
   */
  const checkDeviceSubscription = async (): Promise<void> => {
    if (!isSupported || Notification.permission !== 'granted') {
      deviceSubscribed.value = false;

      return;
    }

    deviceSubscribed.value = (await getSubscribedEndpoint()) !== null;
  };

  /**
   * Ask for permission, register the service worker and subscribe this
   * browser to Web Push. Never throws — a failure comes back with its reason.
   */
  const subscribe = async (): Promise<PushSubscribeResult> => {
    if (!isSupported) {
      return { ok: false, reason: 'unsupported' };
    }

    if (!isConfigured) {
      return { ok: false, reason: 'not-configured' };
    }

    try {
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        return { ok: false, reason: 'denied' };
      }

      const registration = await registerServiceWorker();

      const stale = await registration.pushManager.getSubscription();

      if (stale && !isOwnSubscription(stale)) {
        await stale.unsubscribe();
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      const { endpoint, keys } = subscription.toJSON();

      if (!endpoint || !keys?.p256dh || !keys?.auth) {
        console.error('Push subscription is missing endpoint or encryption keys');

        return { ok: false, reason: 'failed' };
      }

      return {
        ok: true,
        subscription: {
          endpoint,
          p256dh: keys.p256dh,
          auth: keys.auth,
          userAgent: navigator.userAgent,
        },
      };
    } catch (error) {
      console.error(error);

      return { ok: false, reason: 'failed' };
    }
  };

  /**
   * Drop this browser's push subscription. Returns the endpoint that was
   * unsubscribed (so it can be unregistered on the API), or null when there
   * was none.
   */
  const unsubscribe = async (): Promise<string | null> => {
    if (!isSupported) {
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();

      if (!subscription) {
        return null;
      }

      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();

      return endpoint;
    } catch (error) {
      console.error(error);

      return null;
    }
  };

  return {
    isSupported,
    isConfigured,
    isDeviceSubscribed: readonly(deviceSubscribed),
    checkDeviceSubscription,
    subscribe,
    getSubscribedEndpoint,
    unsubscribe,
  };
};
